/**
 * Browser glue for "My location" (issue #161): `navigator.geolocation` +
 * best-effort device-orientation compass access. DOM/browser-facing only —
 * the lat/lon -> pose maths lives in the DOM-free `myLocation.ts` so it
 * stays unit-testable in plain Node.
 *
 * One-shot only, by design (explicit non-goal of #161): `readCurrentPosition`
 * wraps `getCurrentPosition`, never `watchPosition` — no continuous tracking
 * loop, no per-frame cost (issue #17).
 */
import type { MyLocationFailureReason } from './myLocation';

/** True when the browser exposes `navigator.geolocation` — the control is
 * hidden outright when this is false (issue #161's own requirement). */
export function geolocationAvailable(): boolean {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator;
}

export interface PositionReading {
  latitude: number;
  longitude: number;
  /** `coords.accuracy`, metres, if the browser reports one. */
  accuracyM?: number;
}

export type PositionResult =
  | { kind: 'success'; position: PositionReading }
  | { kind: 'failure'; reason: MyLocationFailureReason; message: string };

const DEFAULT_TIMEOUT_MS = 10000;

function mapGeolocationError(error: GeolocationPositionError): { reason: MyLocationFailureReason; message: string } {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return {
        reason: 'permission_denied',
        message: 'Location permission was denied — enable location access for this site in your browser settings to use "My location".',
      };
    case error.TIMEOUT:
      return {
        reason: 'timeout',
        message: 'Could not get a location fix in time — try again.',
      };
    case error.POSITION_UNAVAILABLE:
    default:
      return {
        reason: 'position_unavailable',
        message: 'Could not get a location fix — try again.',
      };
  }
}

/**
 * One-shot GPS fix via `getCurrentPosition`, bounded by `timeoutMs` (issue
 * #161: "never hang"). Resolves — never rejects — a discriminated result so
 * every caller handles every case explicitly; a non-secure context
 * (`window.isSecureContext === false`) is reported as its own failure kind
 * rather than left to whatever the browser's rejection happens to say.
 */
export function readCurrentPosition(timeoutMs = DEFAULT_TIMEOUT_MS): Promise<PositionResult> {
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return Promise.resolve({
      kind: 'failure',
      reason: 'insecure_context',
      message: 'Location needs a secure (HTTPS) connection, which this page is not on.',
    });
  }
  if (!geolocationAvailable()) {
    return Promise.resolve({
      kind: 'failure',
      reason: 'position_unavailable',
      message: 'This browser does not support geolocation.',
    });
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          kind: 'success',
          position: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyM: position.coords.accuracy,
          },
        });
      },
      (error) => resolve({ kind: 'failure', ...mapGeolocationError(error) }),
      { timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

export interface HeadingReading {
  /** Compass heading, degrees, 0 = north, clockwise. */
  headingDeg: number;
}

/** iOS 13+ gates `DeviceOrientationEvent` behind an explicit permission
 * prompt; older/other engines expose no such method at all. */
type DeviceOrientationEventCtor = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>;
};

/** Safari's non-standard compass heading, present on `deviceorientation`
 * events on iOS in place of a reliable `absolute`/`alpha` pair. */
type WebkitOrientationEvent = DeviceOrientationEvent & { webkitCompassHeading?: number };

/**
 * Best-effort device compass heading (issue #161's "no orientation" / "iOS
 * permission denied or unsupported" rows): resolves `null` rather than
 * throwing or rejecting when unsupported, denied, or no event with a usable
 * heading arrives within `timeoutMs` — callers must treat `null` exactly
 * like "no heading available" and fall back to a default bearing; the
 * position jump never blocks on this (issue #161: "never block the position
 * jump on missing compass").
 *
 * Magnetic-vs-true-north correction is explicitly out of scope (issue
 * #161): whatever heading the device reports is used as-is.
 */
export function readCompassHeading(timeoutMs = 2000): Promise<HeadingReading | null> {
  if (typeof window === 'undefined' || typeof DeviceOrientationEvent === 'undefined') {
    return Promise.resolve(null);
  }

  const ctor = DeviceOrientationEvent as DeviceOrientationEventCtor;
  const requestPermission = typeof ctor.requestPermission === 'function' ? ctor.requestPermission.bind(ctor) : null;

  const listenOnce = (): Promise<HeadingReading | null> =>
    new Promise((resolve) => {
      let settled = false;
      const finish = (result: HeadingReading | null) => {
        if (settled) return;
        settled = true;
        window.removeEventListener('deviceorientationabsolute', onEvent);
        window.removeEventListener('deviceorientation', onEvent);
        clearTimeout(timer);
        resolve(result);
      };
      const onEvent = (event: Event) => {
        const orientation = event as WebkitOrientationEvent;
        if (typeof orientation.webkitCompassHeading === 'number') {
          finish({ headingDeg: orientation.webkitCompassHeading });
          return;
        }
        // `alpha` is only a true compass heading when `absolute` is set —
        // otherwise it is relative to wherever the device happened to be
        // pointed at load time, which is exactly issue #161's "absolute
        // false with no compass heading" fallback row. `alpha` counts
        // anticlockwise from device-north; 360 - alpha converts to the
        // clockwise compass bearing `bearingDegFromForward` expects.
        if (orientation.absolute && typeof orientation.alpha === 'number') {
          finish({ headingDeg: (360 - orientation.alpha) % 360 });
        }
      };
      const timer = setTimeout(() => finish(null), timeoutMs);
      window.addEventListener('deviceorientationabsolute', onEvent);
      window.addEventListener('deviceorientation', onEvent);
    });

  if (!requestPermission) return listenOnce();

  return requestPermission()
    .then((state) => (state === 'granted' ? listenOnce() : null))
    .catch(() => null);
}
